/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef } from "react";
import { RoomEvent } from "livekit-client";
import { ChatMessageOutput, useChatHistoryQuery } from "@/graphql/generated";
import { useCachedQuery } from "@/graphql/useCached";
import { useLivekitContext } from "@/context/features/livekit/LivekitRoomContext";
import { toaster } from "@/utils/toaster";
import { useTranslation } from "react-i18next";
import OpenChat from "@/components/elements/open-chat";
import { selectCurrentChat } from "@/context/features/chat/chatSlice";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentAuth } from "@/context/features/auth/authSlice";
import { Role } from "@/types/role.enum";
import { useIsMobile } from "../responsive/useIsMobile";
import {
  selectCurrentRoom,
  setUnreadMessage,
} from "@/context/features/room/roomSlice";
import { useApolloClient } from "@apollo/client";

export function useChatSync() {
  const { livekitContext } = useLivekitContext();
  const { openedGroup } = useSelector(selectCurrentChat);
  const { user } = useSelector(selectCurrentAuth);
  const { roomData } = useSelector(selectCurrentRoom);
  const apollo = useApolloClient();

  const { dataVar, refetch } = useCachedQuery(useChatHistoryQuery, {
    variables: openedGroup?.id ? { groupId: openedGroup.id } : undefined,
    skip: !openedGroup?.id,
  });
  const { t } = useTranslation();
  const { room } = livekitContext ?? {};
  const isMobile = useIsMobile();
  const dispatch = useDispatch();

  const isSecure =
    typeof window !== "undefined" &&
    (window.location.protocol === "https:" ||
      window.location.hostname === "localhost");

  const hasNotificationAPI =
    typeof window !== "undefined" && "Notification" in window;

  const isBackground = () => {
    if (typeof document === "undefined") return false;
    return document.visibilityState !== "visible" || !document.hasFocus();
  };

  useEffect(() => {
    if (!hasNotificationAPI || !isSecure) return;
    if (Notification.permission !== "default") return;

    const handler = async () => {
      try {
        await Notification.requestPermission();
        // console.log("[notif] permission result:", res);
      } catch (e) {
        console.error("[notif] requestPermission error:", e);
      } finally {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        window.removeEventListener("click", handler, { once: true } as any);
      }
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    window.addEventListener("click", handler, { once: true } as any);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return () => window.removeEventListener("click", handler as any);
  }, [hasNotificationAPI, isSecure]);

  const ensurePermission = async () => {
    if (!hasNotificationAPI || !isSecure) return false;
    if (Notification.permission === "granted") return true;
    if (Notification.permission === "denied") return false;
    try {
      const res = await Notification.requestPermission();
      return res === "granted";
    } catch {
      return false;
    }
  };

  const lastNotifyRef = useRef<Record<string, number>>({});
  const shouldNotifyGroup = (groupId: string, cooldownMs = 3000) => {
    const now = Date.now();
    const last = lastNotifyRef.current[groupId] ?? 0;
    if (now - last >= cooldownMs) {
      lastNotifyRef.current[groupId] = now;
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (!room || !user) {
      return;
    }

    const handleMessage = async (payload: Uint8Array) => {
      try {
        const json = new TextDecoder().decode(payload);
        const parsed = JSON.parse(json);

        if (
          parsed?.type === "chat_cleared" &&
          parsed?.roomName === roomData?.name
        ) {
          try {
            apollo.cache.evict({ id: "ROOT_QUERY", fieldName: "chat" });
            apollo.cache.gc();
          } catch (e) {
            console.error(e);
          }
          await refetch();
          return;
        }

        if (parsed?.type !== "chat") return;

        const msg: ChatMessageOutput = {
          __typename: "ChatMessageOutput",
          senderId: parsed.senderId,
          senderName: parsed.senderName,
          text: parsed.text,
          roomName: parsed.roomName,
          groupId: parsed.groupId,
          timestamp: parsed.timestamp,
        };

        const current = dataVar();
        const history = current?.chat?.chatHistory ?? [];
        const dedupeKey = `${msg.senderId}-${msg.timestamp}`;
        if (history.some((m) => `${m.senderId}-${m.timestamp}` === dedupeKey)) {
          return;
        }

        dataVar({
          ...current,
          chat: {
            __typename: "ChatQueries",
            ...current?.chat,
            chatHistory: [...history, msg],
          },
        });

        const isHost =
          String(user?.role ?? "")
            .trim()
            .toUpperCase() === Role.HOST;
        const isSelf = String(user?.id) === String(parsed?.senderId);
        const isForOpenedGroup =
          String(parsed?.groupId) === String(openedGroup?.id);

        // In-app toast (same logic you had)
        if (!isSelf && (isHost || isForOpenedGroup)) {
          dispatch(setUnreadMessage({ gp: parsed?.groupId }));
          toaster(
            "default",
            `${t("new_message_from")} ${parsed?.senderName || parsed?.senderId}:`,
            {
              duration: isMobile ? 2000 : 20000,
              position: isMobile ? "top-right" : "bottom-right",
              description: parsed?.text,
              descriptionClassName:
                "font-medium dark:text-white mt-0 sm:mt-2 pb-2 sm:pb-10 truncate max-w-68 inline-block align-middle",
              action: (
                <OpenChat
                  toastId={`${parsed?.timestamp}${parsed?.senderId}`}
                  gpId={parsed.groupId}
                />
              ),
              id: `${parsed?.timestamp}${parsed?.senderId}`,
            }
          );
        }

        if (!isSelf && isBackground()) {
          // console.log("[notif] background state true");
          if (!hasNotificationAPI) {
            // console.log("[notif] Notification API not available");
          } else if (!isSecure) {
            // console.log(
            //   "[notif] Not a secure context (need https or localhost)"
            // );
          } else if (shouldNotifyGroup(String(parsed?.groupId))) {
            const ok = await ensurePermission();
            // console.log(
            //   "[notif] permission ok?",
            //   ok,
            //   "state:",
            //   Notification.permission
            // );
            if (ok) {
              const n = new Notification(
                `${t("new_message_from")} ${parsed?.senderName || parsed?.senderId}`,
                {
                  body: parsed?.text || "...",
                  icon: "/favicon.ico",
                  tag: String(parsed?.groupId),
                  requireInteraction: true,
                }
              );
              n.onclick = () => {
                try {
                  window.focus();
                } catch (e) {
                  console.error(e);
                }
              };
              // console.log("[notif] created");
            }
          }
        }
      } catch (err) {
        console.warn("خطا در parsing پیام چت:", err);
      }
    };

    room.on(RoomEvent.DataReceived, handleMessage);
    // console.log("[chatSync] listener attached");

    return () => {
      room.off(RoomEvent.DataReceived, handleMessage);
    };
  }, [
    room,
    dataVar,
    refetch,
    apollo,
    roomData?.name,
    openedGroup?.id,
    user?.id,
    user?.role,
    isMobile,
    dispatch,
    t,
    hasNotificationAPI,
    isSecure,
  ]);
}
